import { IContactAdvancedFilters } from 'src/pages/Contacts/CustomHooks/useContactAdvancedFilters';
import Actions from 'src/components/Actions';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon/FontAwesomeIcon';
import ActiveFilterPills from 'src/pages/Contacts/ActiveFilterPills/ActiveFilterPills';
import EmailSelfServiceDialogue from 'src/components/ContactsTable/EmailSelfServiceDialogue/EmailSelfServiceDialogue';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import GenericSearchBar from 'src/components/GenricSearchBar/GenericSearchBar';
import Callout from 'src/components/InformationCallout/Callout';
import classNames from 'classnames';
import './ContactsTableActions.scss';
import { USER_ROLES } from 'src/common/constants';

interface ContactsTableActionsProps {
  onAddContact: () => void;
  onShowFilterModal?: () => void;
  hasActiveFilters: boolean;
  resetFilters: () => void;
  selectedContacts: any[];
  contacts: IContact[];
  onSendInvitation: () => void;
  activeFilters: IContactAdvancedFilters;
  removeSingleActiveFilter: any;
  basicContactFilterString: string;
  updateBasicContactFilterString: (value: string) => void;
  isLoading: boolean;
}

const ContactsTableActions = ({
  onAddContact,
  onShowFilterModal,
  hasActiveFilters,
  resetFilters,
  selectedContacts,
  contacts,
  onSendInvitation,
  activeFilters,
  removeSingleActiveFilter,
  basicContactFilterString,
  updateBasicContactFilterString,
  isLoading,
}: ContactsTableActionsProps) => {
  const [isEmailSelfServiceOpen, setIsEmailSelfServiceOpen] =
    useState<boolean>(false);

  const authenticatedUser: any = useSelector(selectAuthenticatedUser);

  const currentUserRoleName: string | undefined =
    authenticatedUser?.roles?.[0]?.role?.name;

  const canManageEmailSelfService: boolean =
    currentUserRoleName === USER_ROLES.ADMIN ||
    currentUserRoleName === USER_ROLES.SUPER_ADMIN;

  const hasBasicSearchTerm: boolean = !!basicContactFilterString && basicContactFilterString.length > 0;

  return (
    <div className="contactsActionBtn">
      <Actions compact>
        <div>
          <GenericSearchBar
            onSearch={(e: any) => updateBasicContactFilterString(e.target.value)}
            placeholder="Basic Search..."
            disabled={hasActiveFilters}
            stackVertically={hasActiveFilters}
          >
            <div className="filterContainer">
              <div
                className={classNames('filterButtons', {
                  'filter-buttons-loading': isLoading,
                })}
              >
                {canManageEmailSelfService && (
                  <Button
                    color="teal"
                    label="Manage Email Self-Service"
                    onClick={() => setIsEmailSelfServiceOpen(true)}
                    icon={
                      <Icon glyph="envelope" weight="regular" size="small" />
                    }
                    size="small"
                    disabled={isLoading}
                  />
                )}
                <Button
                  color="teal"
                  label="Add Contact"
                  onClick={onAddContact}
                  icon={
                    <Icon glyph="user-plus" weight="regular" size="small" />
                  }
                  size="small"
                  disabled={isLoading}
                />
                {onShowFilterModal && (
                  <Button
                    color="teal"
                    label="Advanced Filter"
                    onClick={onShowFilterModal}
                    icon={<Icon glyph="filter" weight="regular" size="small" />}
                    size="small"
                    disabled={isLoading || hasBasicSearchTerm}
                  />
                )}
                {!isLoading && hasActiveFilters && (
                  <Button
                    label="Clear ALl Filters"
                    onClick={resetFilters}
                    icon={<Icon glyph="filter" weight="regular" size="small" />}
                    size="small"
                  />
                )}
                {selectedContacts && selectedContacts.length > 0 && (
                  <Button
                    color="teal"
                    label="Send invitation link"
                    onClick={onSendInvitation}
                    icon={
                      <Icon
                        glyph="share-square"
                        weight="regular"
                        size="small"
                      />
                    }
                    size="small"
                  />
                )}
              </div>
              <ActiveFilterPills
                activeFilters={activeFilters}
                removeSingleActiveFilter={removeSingleActiveFilter}
              />
            </div>
          </GenericSearchBar>
          {hasBasicSearchTerm && (
            <Callout
              variant="info"
              title="You have a basic search term applied"
              subtitle="Clear your basic search term above to use advanced layered filters."
            />
          )}
          {hasActiveFilters && (
            <Callout
              variant="info"
              title="You have advanced filters applied"
              subtitle="Clear your advanced filters above to use basic search."
            />
          )}
        </div>
      </Actions>
      <EmailSelfServiceDialogue
        isOpen={isEmailSelfServiceOpen}
        onClose={() => setIsEmailSelfServiceOpen(false)}
      />
    </div>
  );
};

export default ContactsTableActions;
